import { CUSTOM_ELEMENTS_SCHEMA, NgModule, NO_ERRORS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { OverlayModule } from '@angular/cdk/overlay';
import { PortalModule } from '@angular/cdk/portal';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTableModule } from '@angular/material/table';
import { MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CdkMenuModule } from '@angular/cdk/menu';
import { ZenRadioComponent } from './controls/zen-radio/zen-radio.component';
import { ZenMenuComponent } from './controls/zen-menu/zen-menu.component';
import { ZenMenuTriggerDirective } from './directives/zen-menu-trigger.directive';
import { MenuItemDirective } from './directives/menu-item.directive';
import { ZenTranslationInputComponent } from './controls/zen-translation-input/zen-translation-input.component';
import { ZenInputComponent } from './controls/zen-input/zen-input.component';
import { ZenDatepickerComponent } from './controls/zen-datepicker/zen-datepicker.component';
import { ZenSelectComponent } from './controls/zen-select/zen-select.component';
import { ZenToggleComponent } from './controls/zen-toggle/zen-toggle.component';
import { ZenModalComponent } from './components/zen-modal/zen-modal.component';
import { ZenSnackbarComponent } from './components/zen-snackbar/zen-snackbar.component';
import { SnackbarInstanceComponent } from './components/zen-snackbar/snackbar-instance/snackbar-instance.component';
import { ZDrawerComponent } from './components/zen-drawer/zen-drawer.component';
import { ZenInputImageComponent } from './controls/zen-input-image/zen-input-image.component';
import { DualDropdownSelectionComponent } from './controls/dual-dropdown-selection/dual-dropdown-selection.component';
import { ZenModalStepperComponent } from './components/zen-modal-stepper/zen-modal-stepper.component';
import { ZenTextareaComponent } from './controls/zen-textarea/zen-textarea.component';
import { ZenFileuploadComponent } from './controls/zen-fileupload/zen-fileupload.component';
import { StickyDirective } from './directives/sticky.directive';
import { MdDashletComponent } from './components/md-dashlet/md-dashlet.component';
import { SidebarContainerComponent } from './components/containers/sidebar-container/sidebar-container.component';
import { ContentContainerComponent } from './components/containers/content-container/content-container.component';
import { ZenMainContainerComponent } from './components/containers/zen-main-container/zen-main-container.component';
import { BackButtonComponent } from './components/common/back-button/back-button.component';
import { SectionSidebarComponent } from './components/common/section-sidebar/section-sidebar.component';
import { ZenEditContainerComponent } from './components/zen-edit-container/zen-edit-container.component';
import { EditContainerDirective } from './components/zen-edit-container/edit-container.directive';
import { ButtonStyleDirective } from './directives/button-style.directive';
import { SectionHeadComponent } from './components/common/section-head/section-head.component';
import { MenuButtonComponent } from './components/common/menu-button/menu-button.component';
import { ZenTreeComponent } from './components/zen-tree/zen-tree.component';
import { AccordionsComponent } from './components/accordions/accordions.component';
import { AccordionComponent } from './components/accordions/accordion/accordion.component';
import { ZenCdkDetachDirective } from './services/zen-cdk-detach.directive';
import { SecondaryTableComponent } from './components/s-table/s-table.component';
import { ZenInputV2Component } from './controls/zen-new-input/zen-new-input.component';
import { ZenTranslationTextareeaComponent } from './controls/zen-translation-textarea/zen-translation-textarea.component';
import { ZenTranslationEditorComponent } from './controls/zen-translation-editor/zen-translation-editor.component';
import { ZenTranslationModalComponent } from './controls/zen-translation-modal/zen-translation-modal.component';
import { ExtractPipe } from '../pipes/extract.pipe';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ExtractNameLanguagePipe } from '../pipes/extract-name-language.pipe';
import { LanguagePipe } from '../pipes/language.pipe';
import { ToFixedPipe } from '../pipes/to-fixed.pipe';
import { TranslatePipe } from '../pipes/translate.pipe';
import { NaPipe } from '../pipes/na.pipe';
import { SafePipe } from '../pipes/safe.pipe';
import { NumberCommaPipe } from '../pipes/number-comma.pipe';

import { ClickOutsideDirective } from '../click-outside.directive';
import { AiStatementGenerationDirective, AiTextValidationDirective } from '../directives/ai-text-handling.directive';

import { TableServerOptionsComponent } from '../temp-table/table-server-options/table-server-options.component';
import { CdkTreeModule } from '@angular/cdk/tree';
import { HeaderComponent } from '../header/header.component';
import { FooterComponent } from '../footer/footer.component';
import { NewModalComponent } from '../new-modal/new-modal.component';
import { ModalComponent } from '../modal/modal.component';
import { EmptySectionComponent } from '../empty-section/empty-section.component';
import { RequiredComponent } from '../validation/required/required.component';
import { EmailComponent } from '../validation/email/email.component';
import { HomeContainerComponent } from '../home-container/home-container.component';
import { ZenEditorComponent } from '../zen-editor/zen-editor.component';
import { AngularEditorModule } from '@kolkov/angular-editor';
import { DemoAccountDirective } from '../demo-account/demo-account.directive';
import { DemoSectionComponent } from '../demo-account/demo-section/demo-section.component';
import { DemoPopupComponent } from '../demo-account/demo-popup/demo-popup.component';
import { LoaderComponent } from '../loader/loader.component';
import { MiniLoaderComponent } from '../mini-loader/mini-loader.component';
import { TempTableComponent } from '../temp-table/temp-table.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NoDataOverlayDirective } from '../directives/no-data-overlay.directive';
import { TableComponent } from './components/table/table.component';
import { TablePaginatorComponent } from './components/paginator/paginator.component';
import { CdkTableModule } from '@angular/cdk/table';
import { FullInnerScreenComponent } from './components/containers/full-inner-screen/full-inner-screen.component';
import { InnerFooterComponent } from './components/containers/inner-footer/inner-footer.component';
import { InnerHeaderComponent } from './components/containers/inner-header/inner-header.component';
import { SurveyBreadcrumbsComponent } from './components/survey-breadcrumbs/survey-breadcrumbs.component';
import { SurveyMainContainerComponent } from './components/containers/survey-main-container/survey-main-container.component';
import { SurveyHeaderComponent } from './components/containers/survey-header/survey-header.component';
import { SurveySidebarComponent } from './components/containers/survey-sidebar/survey-sidebar.component';
import { SurveyContentContainerComponent } from './components/containers/survey-content-container/survey-content-container.component';
import { MatTreeModule } from '@angular/material/tree';
import { MatMenuModule } from '@angular/material/menu';
import { SelectComponent } from './controls/select/select.component';
import { MenuComponent } from './core/menu/menu.component';
import { ZenMenuItemDirective } from './core/menu/menu-item.directive';
import { EmailEditorComponent } from '../zen-editor/email-editor/email-editor.component';
import { EditorControlInputComponent } from '../zen-editor/editor-control-input/editor-control-input.component';
import { ShimmerDirective } from './directives/shimmer.directive';
import { ProductTourModule } from '../product-tour/product-tour.module';

@NgModule({
  declarations: [
    ZenInputComponent,
    ZenRadioComponent,
    ZenSelectComponent,
    ZenModalComponent,
    ZenMenuComponent,
    ZenMenuTriggerDirective,
    MenuItemDirective,
    ZenTranslationInputComponent,
    ZenDatepickerComponent,
    ZenToggleComponent,
    ZenSnackbarComponent,
    SnackbarInstanceComponent,
    ZDrawerComponent,
    ZenInputImageComponent,
    DualDropdownSelectionComponent,
    ZenModalStepperComponent,
    ZenTextareaComponent,
    MdDashletComponent,
    ZenFileuploadComponent,
    StickyDirective,
    SectionHeadComponent,
    SectionSidebarComponent,
    MenuButtonComponent,
    SidebarContainerComponent,
    ContentContainerComponent,
    ZenMainContainerComponent,
    BackButtonComponent,
    ZenEditContainerComponent,
    EditContainerDirective,
    ZenTreeComponent,
    ButtonStyleDirective,
    MenuButtonComponent,
    ZenTreeComponent,
    SectionHeadComponent,
    AccordionsComponent,
    AccordionComponent,
    ZenCdkDetachDirective,
    SecondaryTableComponent,
    ZenInputV2Component,
    ZenTranslationTextareeaComponent,
    ZenTranslationEditorComponent,
    ZenTranslationModalComponent,
    ExtractPipe,
    TranslatePipe,
    ExtractNameLanguagePipe,
    ToFixedPipe,
    LanguagePipe,
    NaPipe,
    SafePipe,
    NumberCommaPipe,
    EmptySectionComponent,
    HomeContainerComponent,
    HeaderComponent,
    FooterComponent,
    ModalComponent,
    LoaderComponent,
    MiniLoaderComponent,
    TempTableComponent,
    NewModalComponent,
    RequiredComponent,
    EmailComponent,
    ZenEditorComponent,
    TableServerOptionsComponent,
    DemoAccountDirective,
    DemoSectionComponent,
    DemoPopupComponent,
    ClickOutsideDirective,
    AiStatementGenerationDirective,
    AiTextValidationDirective,
    NoDataOverlayDirective,
    TableComponent,
    TablePaginatorComponent,
    FullInnerScreenComponent,
    InnerFooterComponent,
    InnerHeaderComponent,
    SurveyBreadcrumbsComponent,
    SurveyMainContainerComponent,
    SurveyHeaderComponent,
    SurveySidebarComponent,
    SurveyContentContainerComponent,
    MenuComponent,
    ZenMenuItemDirective,
    SelectComponent,
    EmailEditorComponent,
    EditorControlInputComponent,
    ShimmerDirective
  ],
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    MatTooltipModule,
    MatTableModule,
    MatTreeModule,
    MatMenuModule,
    MatDialogModule,
    MatSnackBarModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatCheckboxModule,
    PortalModule,
    OverlayModule,
    CdkTreeModule,
    AngularEditorModule,
    FontAwesomeModule,
    MatIconModule,

    CdkTableModule,
    CdkMenuModule,
    ProductTourModule

  ],
  exports: [
    ZenInputComponent,
    ZenRadioComponent,
    ZenSelectComponent,
    ZenModalComponent,
    ZenMenuTriggerDirective,
    ZenMenuComponent,
    MenuItemDirective,
    ZenCdkDetachDirective,
    StickyDirective,
    ZenTranslationInputComponent,
    ZenDatepickerComponent,
    ZenToggleComponent,
    ZenSnackbarComponent,
    ZDrawerComponent,
    DualDropdownSelectionComponent,
    ZenModalStepperComponent,
    ZenTextareaComponent,
    MdDashletComponent,
    ZenFileuploadComponent,
    PortalModule,
    SectionHeadComponent,
    SectionSidebarComponent,
    MenuButtonComponent,
    SidebarContainerComponent,
    ContentContainerComponent,
    ZenMainContainerComponent,
    BackButtonComponent,
    ZenEditContainerComponent,
    EditContainerDirective,
    ZenTreeComponent,
    ButtonStyleDirective,
    MenuButtonComponent,
    ZenTreeComponent,
    SectionHeadComponent,
    AccordionsComponent,
    AccordionComponent,
    SecondaryTableComponent,
    ZenInputV2Component,
    ZenTranslationTextareeaComponent,
    ZenTranslationEditorComponent,
    ZenTranslationModalComponent,
    FullInnerScreenComponent,
    InnerFooterComponent,
    InnerHeaderComponent,

    // Re-export Angular Material modules
    MatTooltipModule,
    MatTableModule,
    MatTreeModule,
    MatMenuModule,
    MatDialogModule,
    MatSnackBarModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatCheckboxModule,
    ExtractPipe,
    TranslatePipe,
    ExtractNameLanguagePipe,
    ToFixedPipe,
    LanguagePipe,
    NaPipe,
    SafePipe,
    NumberCommaPipe,
    FormsModule,
    ReactiveFormsModule,
    EmptySectionComponent,
    HomeContainerComponent,
    HeaderComponent,
    FooterComponent,
    ModalComponent,
    LoaderComponent,
    MiniLoaderComponent,
    TempTableComponent,
    NewModalComponent,
    RequiredComponent,
    EmailComponent,
    ZenEditorComponent,
    TableServerOptionsComponent,
    DemoAccountDirective,
    DemoSectionComponent,
    DemoPopupComponent,
    FontAwesomeModule,

    ClickOutsideDirective,
    AiStatementGenerationDirective,
    AiTextValidationDirective,
    NoDataOverlayDirective,
    ZenInputImageComponent,
    TableComponent,
    TablePaginatorComponent,
    SurveyBreadcrumbsComponent,
    SurveyMainContainerComponent,
    SurveyHeaderComponent,
    SurveySidebarComponent,
    SurveyContentContainerComponent,
    MenuComponent,
    ZenMenuItemDirective,
    SelectComponent,
    EmailEditorComponent,
    EditorControlInputComponent,
    ShimmerDirective
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],

})
export class ThemeModule { }